import React from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails, Chip, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MouseIcon from '@mui/icons-material/Mouse';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import TimelineIcon from '@mui/icons-material/Timeline';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useTranslation } from 'react-i18next';

const Documentation: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {t('documentation.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('documentation.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('documentation.contents')}
            </Typography>
            <List dense>
              <ListItem disablePadding>
                <ListItemButton component="a" href="#graph-visualization">
                  <ListItemIcon><TimelineIcon /></ListItemIcon>
                  <ListItemText primary={t('documentation.graph.title')} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component="a" href="#graph-controls">
                  <ListItemIcon><SettingsIcon /></ListItemIcon>
                  <ListItemText primary={t('documentation.controls.title')} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component="a" href="#map-view">
                  <ListItemIcon><MapIcon /></ListItemIcon>
                  <ListItemText primary={t('documentation.map.title')} />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {/* Graph Visualization Section */}
          <Paper sx={{ p: 3, mb: 3 }} id="graph-visualization">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimelineIcon sx={{ fontSize: 30, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4">{t('documentation.graph.title')}</Typography>
            </Box>
            <Typography paragraph>
              {t('documentation.graph.intro')}
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <MouseIcon sx={{ mr: 1 }} /> {t('documentation.graph.interaction')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemIcon><MouseIcon /></ListItemIcon>
                    <ListItemText 
                      primary={t('documentation.graph.hideTitle')}
                      secondary={t('documentation.graph.hideBody')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><VisibilityOffIcon /></ListItemIcon>
                    <ListItemText 
                      primary={t('documentation.graph.historyTitle')}
                      secondary={t('documentation.graph.historyBody')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><RestoreIcon /></ListItemIcon>
                    <ListItemText 
                      primary={t('documentation.graph.restabilizeTitle')}
                      secondary={t('documentation.graph.restabilizeBody')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><VisibilityOffIcon /></ListItemIcon>
                    <ListItemText 
                      primary={t('documentation.graph.clearTitle')}
                      secondary={t('documentation.graph.clearBody')}
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          </Paper>

          {/* Graph Controls Section */}
          <Paper sx={{ p: 3, mb: 3 }} id="graph-controls">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SettingsIcon sx={{ fontSize: 30, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4">{t('documentation.controls.title')}</Typography>
            </Box>
            <Typography paragraph>
              {t('documentation.controls.intro')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('documentation.controls.filtering')}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>{t('graph.types')}:</strong> {t('documentation.controls.types')}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>{t('graph.badLinks')}:</strong> {t('documentation.controls.badLinks')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('documentation.controls.labelsTitle')}:</strong> {t('documentation.controls.labels')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <SearchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('documentation.controls.searchLists')}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>{t('common.actions.search')}:</strong> {t('documentation.controls.search')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('graph.entityList')}:</strong> {t('documentation.controls.entityList')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <HourglassBottomIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('documentation.controls.timeTravel')}
                  </Typography>
                  <Typography variant="body2">
                    {t('documentation.controls.timeTravelBody')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <DownloadIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('documentation.controls.exportSettings')}
                  </Typography>
                  <Typography variant="body2">
                    {t('documentation.controls.exportBody')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Map View Section */}
          <Paper sx={{ p: 3, mb: 3 }} id="map-view">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MapIcon sx={{ fontSize: 30, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4">{t('documentation.map.title')}</Typography>
            </Box>
            <Typography paragraph>
              {t('documentation.map.intro')}
            </Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{t('documentation.map.features')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary={t('documentation.map.markersTitle')}
                      secondary={t('documentation.map.markersBody')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('documentation.map.sidePanelTitle')}
                      secondary={t('documentation.map.sidePanelBody')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('documentation.map.filteringTitle')}
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" component="span" display="block" gutterBottom>
                            {t('documentation.map.filteringBody')}
                          </Typography>
                          <Typography variant="body2" component="span" display="block" gutterBottom>
                            <strong>{t('documentation.map.companySelectedTitle')}:</strong> {t('documentation.map.companySelectedBody')}
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            <strong>{t('documentation.map.addressSelectedTitle')}:</strong> {t('documentation.map.addressSelectedBody')}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Documentation;
